import { PlusIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useFeature } from "@/lib/feature-management";
import type { KeyMessageGroup } from "@/store/news-feed/key-messages/key-message.slice";
import FilterComponentItem from "../FilterComponentItem";
import {
  KeyMessageFilterMenu,
  KeyMessageGroupHeaderMenu,
} from "./KeyMessageGroupFilterMenu";
import PaginatedExpandableList from "./PaginatedExpandableList";

interface KeyMessageGroupItemProps {
  group: KeyMessageGroup;
  isGroupSelected: boolean;
  isGroupExpanded: boolean;
  selectedKeyMessageIds: Set<string>;
  keyMessageGroups: KeyMessageGroup[];
  feedId: string;
  onToggleGroupSelection: (group: KeyMessageGroup) => void;
  onToggleExpandGroups: (groupId: string, expanded: boolean) => void;
  onToggleItemSelection: (itemId: string, group: KeyMessageGroup) => void;
  onCreateKeyMessage: () => void;
  onSetSelectedGroup: (group: { id: string; name: string } | null) => void;
}

/**
 * Renders a single key message group with its items
 */
const KeyMessageGroupItem = ({
  group,
  isGroupSelected,
  isGroupExpanded,
  selectedKeyMessageIds,
  keyMessageGroups,
  feedId,
  onToggleGroupSelection,
  onToggleExpandGroups,
  onToggleItemSelection,
  onCreateKeyMessage,
  onSetSelectedGroup,
}: KeyMessageGroupItemProps) => {
  const displayKmSpinner = useFeature("display-km-spinner", false);
  const hasKeyMessageItems = group.keyMessageItems.length > 0;

  return (
    <div>
      <FilterComponentItem
        className="px-0"
        title={group.name}
        toggleFilter={() => onToggleGroupSelection(group)}
        tabIndex={Number(group.id)}
        selected={isGroupSelected}
        expand={hasKeyMessageItems}
        isExpanded={isGroupExpanded}
        handleExpandClick={(expanded) =>
          onToggleExpandGroups(group.id, expanded)
        }
      >
        <KeyMessageGroupHeaderMenu
          keyMessageGroups={keyMessageGroups}
          setCreateKeyMessageModalOpen={onCreateKeyMessage}
          group={group}
          setSelectedGroup={onSetSelectedGroup}
          feedId={feedId}
        />
      </FilterComponentItem>
      {hasKeyMessageItems ? (
        <PaginatedExpandableList
          isOpen={isGroupExpanded}
          className="pl-6"
          groupId={group.id}
          items={group.keyMessageItems}
        >
          {group.keyMessageItems.map((item) => {
            const isItemSelected = selectedKeyMessageIds.has(item.id);

            return (
              <FilterComponentItem
                title={item.name}
                count={item.total}
                isCountLoading={
                  displayKmSpinner ? (item.isCountLoading ?? false) : false
                }
                toggleFilter={() => {
                  onToggleItemSelection(item.id, group);
                }}
                key={item.id}
                tabIndex={Number(group.id)}
                selected={isItemSelected}
                className="px-0"
                countClassNames="h-5 flex items-center justify-end"
              >
                <KeyMessageFilterMenu
                  item={item}
                  keyMessageGroups={keyMessageGroups}
                  feedId={feedId}
                  setCreateKeyMessageModalOpen={onCreateKeyMessage}
                  setCreateKeyMessageGroupModalOpen={onCreateKeyMessage}
                  setSelectedGroup={onSetSelectedGroup}
                />
              </FilterComponentItem>
            );
          })}
        </PaginatedExpandableList>
      ) : (
        <Button
          variant="ghost"
          size="none"
          className="text-[10px] font-normal flex text-gray-900 pl-6 py-0.5 w-full justify-start"
          onPress={() => {
            onCreateKeyMessage();
            onSetSelectedGroup(group);
          }}
        >
          <PlusIcon size={10} className="text-gray-600 mr-2" />
          New key topic
        </Button>
      )}
    </div>
  );
};

KeyMessageGroupItem.displayName = "KeyMessageGroupItem";

export default KeyMessageGroupItem;
